/*
 * monitoring: the dashboard (`dash`), the monitoring page and the words its
 * service builds (`mon`), and one match under review (`match`) — translated
 * in the Wave 3 pass (2026-09-30).
 *
 * Risk bands, verdicts and "N days left" are the case pages' own keys
 * (`opp.risk`, `opp.verdict`, `time.daysLeft`) and are not repeated here.
 */
export default {
  dash: {
    subtitle: 'What the monitoring found across your portfolio.',
    updated: 'last scan {when}',
    addTrademark: 'Add trademark',
    kpi: {
      label: 'Operations overview',
      watched: 'Trademarks being watched',
      watchedOf: 'of {total}',
      unwatched: '{n} watched by nobody',
      matches: 'Matches found',
      highRisk: 'High-risk matches',
      closeWithin: 'closes within 14 days | close within 14 days',
      nextWindow: 'Next window: {when}',
    },
    recent: {
      title: 'Recent monitoring matches',
      viewAll: 'View all {n}',
      col: {
        detected: 'Detected',
        protected: 'Protected',
        risk: 'Risk',
        registry: 'Juris.',
        classes: 'Classes',
        deadline: 'Deadline',
        status: 'Status',
      },
    },
    deadlines: {
      title: 'Upcoming deadlines',
      badge: '{n} high-risk within 14 days',
      open: 'Open this match',
      viewAll: 'View all matches by deadline',
    },
    registryLastScan: '· last scan {when}',
  },

  mon: {
    subtitle: {
      matched:
        'published trademark matched against your portfolio | published trademarks matched against your portfolio',
      across: 'Across',
      ofYours: 'of your trademarks',
      registries: '{n} registry | {n} registries',
    },
    lastScan: 'Last scan {when}',
    yours: 'Your trademark',
    trademarks: '{n} trademark | {n} trademarks',
    filters: 'Filters',
    addToMonitoring: 'Add to monitoring',
    kpi: {
      label: 'Match summary',
      total: 'Total matches detected',
      judged: '{n} read by the AI judge',
      highHint: 'Require review · {n} closing within 14 days',
      medHint: 'Review recommended',
      lowHint: 'Read when time allows',
    },
    filter: {
      window: 'Can you still act?',
      windowOpen: 'Deadline still open',
      windowUnknown: 'No deadline published',
      windowClosed: 'Deadline passed',
      windowAll: 'Show everything',
      risk: 'Risk',
      registry: 'Registry',
      state: 'Review state',
    },
    state: {
      new: 'Awaiting review',
      reviewed: 'Reviewed',
      dismissed: 'Dismissed',
      escalated: 'Escalated',
    },
    toolbar: {
      shown: '{n} shown',
      grouped: 'Grouped by your trademark',
      sortLabel: 'Sort order',
      sort: 'Closing deadline first',
    },
    intro: {
      bands:
        "Each shaded band below is one of your own trademarks. The rows under it are trademarks the scan found in the registries' bulletins that may conflict with it.",
      openOnly:
        'Showing only the ones whose opposition deadline has not passed — {link}, including {n} whose deadline has gone.',
      showAll: 'show all {n}',
    },
    thead: {
      detected: 'Detected trademark',
      similarity: 'Similarity analysis',
      riskDeadline: 'Risk & deadline',
      actions: 'Actions',
    },
    band: {
      toggle: 'Show or hide the matches for {name}',
      countWord: 'match | matches',
      reg: 'Reg. № {n}',
      active: 'Active monitoring',
      paused: 'Monitoring paused',
      notScanned: 'Not scanned yet',
    },
    logo: '{name} logo',
    empty: 'No matches in this view.',
    clearFilters: 'Clear the filters',
    clearHint: '{link} to see all of them.',
    showing: 'Showing {shown} of {total} matches, closing deadlines first.',
    row: {
      view: 'View details',
      dismiss: 'Dismiss match',
    },
    dismissPrompt:
      'Why is “{name}” not a conflict?\n\nYour reason is stored with the match. Nobody is retrained by it — it is how we measure whether the AI was right, and what the thresholds get tuned against later.',
    rail: {
      label: 'Summary',
      found: 'What the scan found',
      summary:
        'The scan surfaced {matches} against your portfolio, of which {high} read as high risk.',
      matches: '{n} match | {n} matches',
      breakdown: 'Conflict breakdown',
      total: 'All matches',
      decided: 'How these were decided',
      judged: 'Read by the AI judge',
      judgedSub: 'A verdict with its reasoning',
      rule: 'Matched by rule',
      ruleSub: 'Identical or near-identical',
      awaiting: 'Awaiting your review',
      awaitingSub: 'Not yet actioned',
    },
    health: {
      title: 'Monitoring health',
      summary: '{count} trademarks are being watched. Last scan {when}.',
      count: '{n} of {total}',
      unwatched: 'Not watched at all',
      unwatchedSub: 'No registry is being checked for these',
      registries: 'Registries watched',
      workload: 'Workload',
      awaiting: 'Waiting for a decision',
      awaitingSub: 'Nobody has answered these yet',
      next: 'Next window to close',
      nextSub: 'Among matches still unanswered',
    },
    start: {
      title: 'Where to start',
      review: 'Review the {high} first.',
      high: '{n} high-risk match | {n} high-risk matches',
      closingCount: '{n} of them',
      closingTail:
        'has an opposition deadline within 14 days. | have an opposition deadline within 14 days.',
      button: 'Review high-risk matches',
    },

    /* Built by monitoring.service.ts. */
    deadline: {
      today: 'Closes today',
      closedDays: 'Closed {n} day ago | Closed {n} days ago',
      closedMonths: 'Closed {n} month ago | Closed {n} months ago',
      closedYears: 'Closed {n} year ago | Closed {n} years ago',
      on: 'Deadline {date}',
    },
    since: {
      never: 'never',
      now: 'just now',
      minutes: '{n} min ago',
      hours: '{n} hr ago | {n} hrs ago',
      days: '{n} day ago | {n} days ago',
    },
    tag: {
      filed: 'Filed {date}',
      judge: 'AI · {model}',
      judgeUnnamed: 'AI judge',
      rule: 'Rule match',
    },
    /* One name per similarity key the API sends; an unknown key shows the
     * API's own label. */
    channel: {
      visual: 'Visual',
      aural: 'Sound',
      phonetic: 'Sound',
      conceptual: 'Meaning',
      name_semantic: 'Meaning',
      goods: 'Goods',
      name_trigram: 'Spelling',
      name_contains: 'Contained',
      name_stem: 'Shared stem',
      name_edit: 'Near spelling',
      logo_text: 'Words in logo',
      logo_visual: 'Look',
      logo_phash: 'Same picture',
      gate: 'Gate',
      rerank: 'Reranker',
    },
    /* Each belt's evidence as a sentence — the API's `_belt_sentence`, in
     * every language. */
    belt: {
      edit: '{a} and {b} differ by {n} letter over {letters} | {a} and {b} differ by {n} letters over {letters}',
      stemStart: 'Both contain {stem} at the start',
      stemInside: 'Both contain {stem} as a stem',
      stemCoverage: '{text}, covering {pct}% of the word',
      contains: '{inner} appears inside {outer}',
      sound: 'The two names share a sound key ({keys})',
      spelling: 'The spellings overlap ({n} similarity)',
      meaning: 'The names read as related in meaning ({n} similarity)',
      goods: 'The goods and services read as close ({n} similarity)',
      logoWords: 'The same words appear inside the logos: {words}',
      samePicture:
        'The pictures are near-identical ({n} bit apart) | The pictures are near-identical ({n} bits apart)',
      looksAlike: 'The pictures look alike ({n} similarity)',
      rerank: 'The reranker scored it {score}: {band}',
      rerankBand: 'Reranker: {band}',
    },
    rerankBand: {
      floor: 'below the line',
      gray: 'gray zone',
      top: 'top band',
      unscored: 'not scored',
    },
    decision: {
      ceiling: 'Surfaced at once',
      surface: 'Surfaced',
      gray: 'Gray zone',
      floor: 'Below the floor',
      dismiss: 'Dismissed',
    },
    gate: {
      identical: 'identical name',
      edits: 'name within {n} letter | name within {n} letters',
      sound: 'same sound at the same length',
      picture: 'same picture ({n} bit apart) | same picture ({n} bits apart)',
      floor: 'every signal below {line}',
    },
    escalation: {
      notDismissed: 'Not escalated: the first model did not dismiss it',
      below: 'Not escalated: reranker {score} below {line}',
      noSecond: 'Not escalated: reranker {score} ≥ {line}, but no second model was available',
      escalated: 'Escalated: the first model dismissed it, reranker {score} ≥ {line}',
    },
    markType: {
      word: 'Word mark',
      figurative: 'Figurative mark',
      combined: 'Combined mark',
      sound: 'Sound mark',
      other: 'Other',
    },
    brandStatus: {
      registered: 'Registered',
      pending: 'Pending',
      filed: 'Filed',
      published: 'Published',
      opposed: 'Opposed',
      cancellation_procedure: 'Cancellation',
      expired: 'Expired',
      refused: 'Refused',
      withdrawn: 'Withdrawn',
    },
    feedback: {
      confirm: 'Confirmed as a conflict',
      escalate: 'Marked for opposition',
      dismiss: 'Dismissed',
      false_positive: 'Not a conflict',
    },
  },

  match: {
    review: 'Match review',
    title: '{found} vs {ours}',
    subtitle: 'Your trademark {ours} against {registry} publication {found}',
    previous: 'Previous',
    next: 'Next',
    close: 'Close',
    back: 'Back to monitoring',
    yourMark: 'Your monitored trademark',
    position: 'Match {p} of {total}',
    viewTrademark: 'View trademark →',
    compare: 'Trademark comparison',
    found: 'Found in the bulletin',
    vs: 'VS',
    field: {
      classes: 'Nice classes',
      registered: 'Registered',
      applicationNo: 'Application №',
      type: 'Type',
      filed: 'Filed',
      registry: 'Registry',
      name: 'Name',
      owner: 'Owner',
      registrationNo: 'Registration №',
      status: 'Status',
      deadline: 'Opposition deadline',
      vienna: 'Vienna codes',
      designated: 'Designated',
      found: 'Found',
      reviewState: 'Review state',
    },
    nonePublished: 'None published',
    score: {
      judge: 'AI risk score',
      rules: 'Rule match score',
      low: 'Low',
      med: 'Medium',
      high: 'High',
      window: 'Opposition window',
      decidedBy: 'Decided by',
      matchingRules: 'matching rules',
    },
    tab: {
      label: 'Match review sections',
      analysis: 'Why this was found',
      details: 'Both trademarks',
      timeline: 'Timeline',
    },
    analysis: {
      judge: 'What the AI read',
      rules: 'What the rules found',
      judgeIntro:
        'A model read this pair the way an examiner would and gave the four scores below. It is an opinion, not a decision: what it is for is putting the pairs worth your time in front of you.',
      rulesIntro:
        'No model has read this pair. It is here because the matching rules below fired — each one is a check you can verify yourself.',
      firstReading: 'First reading — {model} said {verdict}',
      surfaced: 'Surfaced at once:',
      shared: 'Shared Nice classes: {list}',
    },
    decide: {
      title: 'Your decision',
      intro:
        'Whatever you decide is stored with the match: it is how we measure whether the AI was right, and what the thresholds get tuned against later. Agreeing takes one click. Disagreeing asks for a reason, because that is the answer that tells us something we did not already have.',
      already: 'Already answered:',
      someone: 'Someone',
      disagreed: '· disagreed with the machine',
      saving: 'Saving…',
      confirm: 'Yes, this is a conflict',
      escalate: 'Conflict — mark for opposition',
      orDismiss: 'Or say it is not a conflict, and why:',
      reasons: 'Reason for dismissing',
      own: 'In your own words',
      required: '(required)',
      optional: '(optional)',
      placeholder:
        'e.g. they sell industrial software for oil and gas — no overlap with our consumer product despite the same Nice class on paper.',
      hint: 'Stored with your decision. Free text is kept beside the reason above, not instead of it, because a list can be counted and prose cannot.',
      failed: 'Your answer was not saved. Try again in a moment.',
      dismiss: 'Not a conflict — dismiss',
    },
    reason: {
      different_market: {
        label: 'Different industry or market',
        sub: 'Real use does not overlap',
      },
      weak_sound: {
        label: 'Weak sound relation',
        sub: 'Spelling was weighted over how it reads',
      },
      not_confusable: {
        label: 'No likelihood of confusion',
        sub: 'Not legally relevant in practice',
      },
      coexists: {
        label: 'Already coexists',
        sub: 'Prior coexistence agreement in place',
      },
      visual_difference: {
        label: 'Visual difference dominates',
        sub: 'Logos or get-up are clearly distinct',
      },
      other: {
        label: 'Other',
        sub: 'Say why in your own words',
      },
    },
    details: {
      title: 'The trademark found in the bulletin',
      goods: 'Goods and services in the classes you share',
    },
    timeline: {
      title: 'What happened, and when',
      registered: '{name} registered',
      filed: '{name} filed',
      found: 'Found by the scan',
      read: 'Read by {model}',
      closes: 'Opposition window closes',
    },
    rail: {
      label: 'Match summary',
      this: 'This match',
      applicant: 'This applicant, elsewhere',
      applicantSummary: '{owner} has {filings} that matched your portfolio.',
      sameApplicant: 'The same applicant',
      otherFilings: '{n} other filing | {n} other filings',
      against: 'against {brand}',
      filing: 'This filing, against your others',
      filingSummary: 'The same application also matched {marks} of yours.',
      otherMarks: '{n} other trademark | {n} other trademarks',
      othersAgainst: 'Others against {brand}',
      othersSummary:
        '{n} more filing was found against this trademark. | {n} more filings were found against this trademark.',
      rest: 'Review the rest',
      nextForBrand: 'Next match for this trademark',
      backAll: 'Back to all matches',
    },
  },
} as const
